import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type CrmObject, getPropertyValue } from '@/types/crm';

import { createCRMObjectClient } from '../core/CRMObjectClient';
import { useMeetingPrepBrief } from './useMeetingPrepBrief';
import { deriveMeetingVariant } from './utils/deriveMeetingVariant';
import { isValidViewDefinition } from './utils/meetingPrepTypes';

// Mirrors MeetingDetailSection's view_definition guard: a renderable v2 prep
// brief must (a) parse as JSON, (b) pass the full `isValidViewDefinition`
// structural check, and (c) have `version === '2.0'`. Any other payload
// (invalid JSON, missing fields, older versions, partial writes) renders
// nothing in the section, so it must not count toward hasPrep for the
// success variant.
function hasValidViewDefinition(viewDef: string | undefined): boolean {
  if (!viewDef) {
    return false;
  }
  try {
    const parsed: unknown = JSON.parse(viewDef);
    return isValidViewDefinition(parsed) && parsed.version === '2.0';
  } catch {
    return false;
  }
}

/**
 * Derives the meeting record panel's telemetry `successVariant` — the record
 * panel's own observability scope for `useViewHealthScope('overlay', ...)`.
 * Not meeting-typed objects resolve to `undefined` immediately (no queries
 * fired, since `meetingEntityId` stays empty).
 */
export function useMeetingRecordTelemetry(object: CrmObject | null) {
  const isMeeting = object?.entity_type_name === 'ahoy_meeting';
  const meetingOrgId = object?.org_id ?? 0;
  const meetingEntityId = isMeeting ? (object?.entity_id ?? '') : '';

  const { data: prepBriefs, isLoading: isPrepLoading } = useMeetingPrepBrief(
    meetingOrgId,
    meetingEntityId
  );

  // Distinct query key from MeetingNotesTab's `['meetingNotes', ...]`: the tab
  // fetches a wider property set, so sharing the cache key would let whichever
  // query resolves first poison the other's shape.
  const { data: meetingNotes, isLoading: isNotesLoading } = useQuery({
    queryKey: ['meetingNotesTelemetry', meetingOrgId, meetingEntityId],
    queryFn: async () => {
      const client = createCRMObjectClient(meetingOrgId, 'ahoy_meeting_notes');
      const response = await client.list({
        associated_with_entity_id: meetingEntityId,
        properties: ['ahoy_title', 'ahoy_summary', 'ahoy_created_at'],
        order_by: '-ahoy_created_at',
      });
      return response.results;
    },
    enabled: !!meetingEntityId,
    staleTime: 5 * 60 * 1000,
  });

  const successVariant = useMemo(() => {
    if (!isMeeting) {
      return undefined;
    }
    // Only compute when both queries have settled SUCCESSFULLY — otherwise
    // the variant would flip between PREP_ONLY/NOTES_ONLY/BOTH as queries
    // resolve out of order, and could fire MEETING_NEITHER prematurely on
    // fetch errors (when isLoading flips to false but data is undefined).
    // An empty result is a successful "no records" state and renders as
    // an empty array, so `=== undefined` reliably distinguishes "fetch
    // failed / not yet completed" from "fetched, none found".
    if (
      isPrepLoading ||
      isNotesLoading ||
      prepBriefs === undefined ||
      meetingNotes === undefined
    ) {
      return undefined;
    }
    // hasPrep: the latest prep brief has a non-empty summary or a valid v2.0
    // view_definition. Mirrors MeetingDetailSection, which only renders the
    // structured panel when JSON.parse succeeds AND version === '2.0' — and
    // otherwise falls back to the markdown summary. Treating any non-empty
    // view_definition string as prep would misclassify briefs whose JSON is
    // invalid (and therefore render nothing) as MEETING_PREP_ONLY. We check
    // only `prepBriefs[0]` (the most recent — list is ordered by
    // -ahoy_created_at), matching the section's rendering logic.
    const latestBrief = prepBriefs?.[0];
    const latestSummary = latestBrief
      ? getPropertyValue<string>(latestBrief.properties?.ahoy_summary)
      : undefined;
    const latestViewDef = latestBrief
      ? getPropertyValue<string>(latestBrief.properties?.ahoy_view_definition)
      : undefined;
    const hasPrep =
      Boolean(latestSummary) || hasValidViewDefinition(latestViewDef);
    const hasNotes = Boolean(meetingNotes && meetingNotes.length > 0);
    return deriveMeetingVariant(hasPrep, hasNotes);
  }, [isMeeting, isPrepLoading, isNotesLoading, prepBriefs, meetingNotes]);

  return successVariant;
}
